import UserContext from "Context/UserProvider";
import AdvancedBookingMap from "components/charging/AdvancedBookingMap/AdvancedBookingMap";
import { useContext, useEffect, useState } from "react";
import toast from 'react-hot-toast';
import { useLocation } from "react-router-dom";
import Select from "react-select";
import { 
    Box, 
    Card, 
    CardContent, 
    Typography, 
    Button, 
    Grid, 
    CircularProgress,
    TextField,
    Stack
} from '@mui/material';
import { useApiService } from "../../../services/ApiService/ApiService";
import TimelineIcon from '@mui/icons-material/Timeline';

interface ChargingPartner {
    label: string;
    value: string;
    name?: string;
}

interface ElectricVehicleDriver {
    label: string;
    value: string;
    name?: string;
}

interface Cell {
    status: 'AVAILABLE' | 'UNAVAILABLE' | 'RESERVED';
    originalStatus: 'AVAILABLE' | 'UNAVAILABLE' | 'RESERVED';
    uuid: string | null;
    pill: string;
    connectorUuid: string;
    reserved: boolean;
    available: boolean;
    identifier?: string;
}

interface DetailsPanelProps {}

const AdvancedBookingPanel: React.FC<DetailsPanelProps> = () => {
    const { userInfo } = useContext(UserContext);
    const { fetchWithAuth } = useApiService();
    const [loading, setLoading] = useState(true);
    const [daysLabels] = useState([]) as any;
    const location = useLocation();
    const [chargingPartnerOptions] = useState({ label: "None", value: "NONE", name: "chargingPartner" }) as any;
    const [chargingPartnerValue, setChargingPartnerValue] = useState<ChargingPartner | {}>({});
    const [isDisabled, setIsDisabled] = useState(false);
    const dwellingInfo = location.state as any;
    const [chargePoints, setChargePoints] = useState([]) as any;
    const [selectedCells, setSelectedCells] = useState<Cell[]>([]);
    const [reservationDate, setReservationDate] = useState('');
    const [electricVehicleDrivers, setElectricVehicleDrivers] = useState([]) as any;
    const [electricVehicleDriverValue, setElectricVehicleDriverValue] = useState<ElectricVehicleDriver | {}>({});
    const [chargePointConnectorUuid, setChargePointConnectorUuid] = useState('');

    const handleCellClick = (cell: Cell, chargePointConnectorUuid: string) => {
        if (cell.status === "UNAVAILABLE") {
            return;
        }

        const cellIdentifier = cell.identifier || `${cell.connectorUuid}-${cell.pill}`;
        const isSelected = selectedCells.some(selectedCell => selectedCell.identifier === cellIdentifier);
        setChargePointConnectorUuid(cell.connectorUuid);

        if (isSelected) {
            setSelectedCells(selectedCells.filter(selectedCell => selectedCell.identifier !== cellIdentifier));
            cell.status = cell.originalStatus;
        } else {
            switch (cell.status) {
                case "RESERVED":
                    cell.status = "AVAILABLE";
                    cell.reserved = false;
                    cell.available = true;
                    break;
                case "AVAILABLE":
                    cell.status = "RESERVED";
                    cell.reserved = true;
                    cell.available = false;
                    break;
            }
            setSelectedCells([...selectedCells, { ...cell, identifier: cellIdentifier }]);
        }
    };

    const handleSaveClick = async () => {
        try {
            setIsDisabled(true);
            const response = await fetchWithAuth('/cpo/charge-points/advanced-booking/create', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    selectedCells,
                    reservationDate,
                    businessUuid: userInfo.businessUuid,
                    chargePointConnectorUuid,
                    electricVehicleDriverValue,
                    slots: selectedCells
                })
            });

            if (!response.ok) {
                throw new Error('Failed to save booking');
            }

            toast.success('Advanced Booking saved successfully', {
                position: 'bottom-center',
                duration: 5000,
            });
        } catch (error) {
            console.error('Error saving booking:', error);
            toast.error('Failed to save booking');
        } finally {
            setIsDisabled(false);
        }
    };

    const isCellSelected = (cell: Cell) => {
        const cellIdentifier = `${cell.connectorUuid}-${cell.pill}`;
        return selectedCells.some(selectedCell => selectedCell.identifier === cellIdentifier);
    };

    function checking_null_undefined(array: any) {
        if (array === null || array === undefined) {
            return [];
        }
        return array;
    }

    useEffect(() => {
        const initializeData = async () => {
            try {
                const today = new Date();
                const formattedDate = today.toISOString().substring(0, 10);
                if (!reservationDate) {
                    setReservationDate(formattedDate);
                }

                const response = await fetchWithAuth('/business/electric-vehicle/driver/list', {
                    method: 'POST',
                    body: JSON.stringify({
                        uuid: userInfo.uuid,
                        businessUuid: userInfo.businessUuid
                    })
                });

                if (!response.ok) {
                    throw new Error('Failed to fetch drivers');
                }

                const res_json = await response.json();
                const drivers = res_json.drivers.map((e: any) => ({
                    ...e,
                    label: e.registrationNumber + " / " + e.driver,
                    value: e.uuid,
                    name: "electricVehicleDriver"
                }));
                setElectricVehicleDrivers(drivers);

                if (drivers.length > 0) {
                    setElectricVehicleDriverValue(drivers[0]);
                    await search(formattedDate, drivers[0].value);
                }
            } catch (error) {
                console.error('Error initializing data:', error);
                toast.error('Failed to initialize data');
            } finally {
                setLoading(false);
            }
        };

        initializeData();
    }, []);

    const handleSearchClick = () => {
        if ('value' in electricVehicleDriverValue && electricVehicleDriverValue.value) {
            search(reservationDate, electricVehicleDriverValue.value);
        }
    };

    async function search(date: string, electricVehicleDriverUuid: string) {
        try {
            setLoading(true);
            const response = await fetchWithAuth('/cpo/charge-points/availability/get', {
                method: 'POST',
                body: JSON.stringify({
                    uuid: userInfo.uuid,
                    accountUuid: userInfo.accountUuid,
                    electricVehicleDriverUuid,
                    businessUuid: dwellingInfo?.businessUuid,
                    dwellingUuid: dwellingInfo?.uuid,
                    reservationDate: date
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch availability');
            }

            const res_json = await response.json();
            const chargePoints = checking_null_undefined(res_json.chargePoints);
            setChargePoints(chargePoints.map((e: any) => ({ ...e, id: e.uuid })));
        } catch (error) {
            console.error('Error fetching availability:', error);
            toast.error('Failed to fetch availability');
        } finally {
            setLoading(false);
        }
    }

    function onChange(e: any) {
        if (e.name === "chargingPartner") {
            const selectedChargingPartner = chargingPartnerOptions.find((i: any) => i.uuid === e.uuid);
            if (selectedChargingPartner != null) {
                const selectedValue = {
                    label: selectedChargingPartner.name,
                    value: selectedChargingPartner.uuid,
                    ...selectedChargingPartner,
                };
                setChargingPartnerValue(selectedValue);
            }
        } else if (e.name === "electricVehicleDriver") {
            const selectedElectricVehicleDriver = electricVehicleDrivers.find((i: any) => i.uuid === e.uuid);
            if (selectedElectricVehicleDriver != null) {
                const selectedValue = {
                    label: selectedElectricVehicleDriver.registrationNumber + " / " + selectedElectricVehicleDriver.driver,
                    value: selectedElectricVehicleDriver.uuid,
                    ...selectedElectricVehicleDriver,
                };
                setElectricVehicleDriverValue(selectedValue);
            }
        } else if (e.target.name === "reservationDate") {
            setReservationDate(e.target.value);
        }
    }

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <TimelineIcon sx={{ mr: 1 }} />
                                <Typography variant="h6">Selection Criteria</Typography>
                            </Box>
                            <Stack spacing={3}>
                                <Grid container spacing={2} alignItems="center">
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ minWidth: { xs: 'auto', sm: 120 } }}>Vehicle / Driver:</Typography>
                                            <Box sx={{ flex: 1 }}>
                                                <Select
                                                    menuPortalTarget={document.body}
                                                    styles={{ container: (base) => ({ ...base, width: "100%" }) }}
                                                    value={electricVehicleDriverValue}
                                                    name="electricVehicleDriver"
                                                    onChange={onChange}
                                                    options={electricVehicleDrivers}
                                                />
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography sx={{ minWidth: { xs: 'auto', sm: 128 } }}>Reservation Date:</Typography>
                                            <Box sx={{ flex: 1 }}>
                                                <TextField
                                                    type="date"
                                                    value={reservationDate}
                                                    onChange={(e) => setReservationDate(e.target.value)}
                                                    fullWidth
                                                    size="small"
                                                    InputLabelProps={{
                                                        shrink: true,
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={4}>
                                        <Box sx={{ display: 'flex', justifyContent: { xs: 'center', md: 'flex-start' } }}>
                                            <Button
                                                variant="contained"
                                                disabled={isDisabled}
                                                onClick={handleSearchClick}
                                                fullWidth
                                            >
                                                Search
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>

                {loading ? (
                    <Grid item xs={12}>
                        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                            <CircularProgress />
                        </Box>
                    </Grid>
                ) : (
                    <>
                        {chargePoints.length > 0 && chargePoints.map((chargePoint: any, index: number) => (
                            <Grid item xs={12} key={index}>
                                <AdvancedBookingMap
                                    data={chargePoint}
                                    labels={daysLabels}
                                    onCellClick={handleCellClick}
                                    isCellSelected={isCellSelected}
                                />
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'center', 
                                mt: 2,
                                px: { xs: 1, sm: 2 }
                            }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveClick}
                                    disabled={isDisabled}
                                    fullWidth
                                    sx={{ maxWidth: { sm: '400px' } }}
                                >
                                    Save Selected Cells
                                </Button>
                            </Box>
                        </Grid>
                    </>
                )}
            </Grid>
        </Box>
    );
}

export default AdvancedBookingPanel;
